import React, { useState } from 'react';
import { 
  Box, 
  BottomNavigation, 
  BottomNavigationAction, 
  Fab, 
  Drawer, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText, 
  Typography, 
  Avatar, 
  Divider,
  Chip,
  LinearProgress
} from '@mui/material';
import { 
  faHome, 
  faGraduationCap, 
  faBook, 
  faTrophy,
  faIdCard,
  faBars,
  faUser,
  faCog,
  faSignOutAlt,
  faStar
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';

const MembroMenuMobile: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const navigationItems = [
    { label: 'Início', path: '/membro', icon: faHome },
    { label: 'Lições', path: '/licao', icon: faGraduationCap },
    { label: 'Declaração', path: '/declaracao', icon: faBook },
    { label: 'Rank', path: '/rank', icon: faTrophy },
    { label: 'Carteirinha', path: '/carteirinha', icon: faIdCard }
  ];

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/inicio/login');
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
    setDrawerOpen(false);
  };

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  return (
    <>
      {/* Floating Action Button for Menu */}
      <Fab
        color="primary"
        aria-label="menu"
        onClick={toggleDrawer}
        sx={{
          position: 'fixed',
          bottom: 80,
          right: 16,
          zIndex: 1000,
          background: 'linear-gradient(135deg, #225477 0%, #4688ae 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #1a4560 0%, #3a7a9e 100%)'
          }
        }}
      >
        <FontAwesomeIcon icon={faBars} />
      </Fab>

      {/* Drawer Menu */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={toggleDrawer}
        PaperProps={{
          sx: {
            width: 280,
            backgroundColor: '#fff'
          }
        }}
      >
        <Box sx={{ p: 3, background: 'linear-gradient(135deg, #225477 0%, #4688ae 100%)', color: 'white', textAlign: 'center' }}>
          <Avatar 
            src={user?.perfil || '/assets/images/perfil.png'} 
            alt="Perfil"
            sx={{ 
              width: 80, 
              height: 80, 
              mx: 'auto', 
              mb: 2,
              border: '3px solid rgba(255,255,255,0.3)'
            }}
          />
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            {user?.nome || 'Usuário'}
          </Typography>
          <Chip 
            label="Membro Ativo" 
            size="small" 
            sx={{ 
              backgroundColor: 'rgba(255,255,255,0.2)', 
              color: 'white',
              fontSize: '0.75rem'
            }} 
          />
          
          {/* Progress Bar */}
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                Progresso Geral
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                60%
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={60} 
              sx={{ 
                height: 6, 
                borderRadius: 3,
                backgroundColor: 'rgba(255,255,255,0.2)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#FFD700'
                }
              }} 
            />
          </Box>
        </Box>

        <List sx={{ pt: 2 }}>
          {navigationItems.map((item) => (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                onClick={() => { navigate(item.path); setDrawerOpen(false); }}
                selected={location.pathname === item.path}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 2,
                  '&.Mui-selected': {
                    backgroundColor: '#225477',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#1a4560'
                    },
                    '& .MuiListItemIcon-root': {
                      color: 'white'
                    }
                  },
                  '&:hover': {
                    backgroundColor: '#f5f7fa'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <FontAwesomeIcon 
                    icon={item.icon} 
                    style={{ 
                      fontSize: '1.2rem',
                      color: location.pathname === item.path ? 'white' : '#225477'
                    }} 
                  />
                </ListItemIcon>
                <ListItemText 
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: '0.95rem',
                    fontWeight: location.pathname === item.path ? 600 : 500
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        <Divider sx={{ my: 2 }} />

        <List>
          <ListItem disablePadding>
            <ListItemButton
              sx={{
                mx: 1,
                borderRadius: 2,
                '&:hover': {
                  backgroundColor: '#f5f7fa'
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <FontAwesomeIcon icon={faCog} style={{ fontSize: '1.1rem', color: '#666' }} />
              </ListItemIcon>
              <ListItemText 
                primary="Configurações"
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  color: '#666'
                }}
              />
            </ListItemButton>
          </ListItem>
          
          <ListItem disablePadding>
            <ListItemButton
              onClick={handleLogout}
              sx={{
                mx: 1,
                borderRadius: 2,
                '&:hover': {
                  backgroundColor: '#ffebee'
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <FontAwesomeIcon icon={faSignOutAlt} style={{ fontSize: '1.1rem', color: '#d32f2f' }} />
              </ListItemIcon>
              <ListItemText 
                primary="Sair"
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  color: '#d32f2f'
                }}
              />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>

      {/* Bottom Navigation */}
      <BottomNavigation
        value={location.pathname}
        onChange={(event, newValue) => {
          navigate(newValue);
        }}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          backgroundColor: '#fff',
          borderTop: '1px solid #e0e0e0',
          boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
          '& .MuiBottomNavigationAction-root': {
            minWidth: 'auto',
            padding: '6px 12px 8px',
            '&.Mui-selected': {
              color: '#225477'
            }
          }
        }}
      >
        {navigationItems.map((item) => (
          <BottomNavigationAction
            key={item.path}
            label={item.label}
            value={item.path}
            icon={
              <FontAwesomeIcon 
                icon={item.icon} 
                style={{ 
                  fontSize: '1.1rem',
                  color: location.pathname === item.path ? '#225477' : '#666'
                }} 
              />
            }
          />
        ))}
      </BottomNavigation>
    </>
  );
};

export default MembroMenuMobile; 